import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  AcademicCapIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import CourseCard from '../components/CourseCard';
import SEO from '../components/SEO';
import { courses, courseCategories } from '../data/courses';
import { courseStructuredData, faqStructuredData } from '../data/seo';

const Courses = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('name');

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'All' || 
                           courseCategories.find(cat => cat.name === selectedCategory)?.courses.includes(course.id);
    
    return matchesSearch && matchesCategory;
  });

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.title.localeCompare(b.title);
      case 'price':
        return parseInt(a.price.replace('$', '')) - parseInt(b.price.replace('$', ''));
      case 'duration':
        return parseInt(a.duration.split('-')[0]) - parseInt(b.duration.split('-')[0]);
      default:
        return 0;
    }
  });

  const coursesFAQ = [
    {
      question: "What programming languages do you teach?",
      answer: "I teach Python, JavaScript, React, HTML/CSS, and cover DevOps tools like Docker and Kubernetes. I also specialize in Machine Learning and Data Analysis."
    },
    {
      question: "Are the courses suitable for beginners?",
      answer: "Yes! I offer courses for all skill levels, from complete beginners to advanced developers looking to upskill."
    },
    {
      question: "How long does each course take?",
      answer: "Course duration varies from 6-16 weeks depending on the subject and your learning pace. Each session is typically 1-2 hours."
    },
    {
      question: "Do you provide certificates?",
      answer: "While I don't provide formal certificates, I can provide letters of recommendation and help you build a portfolio that demonstrates your skills."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title="Programming Courses - Python, React, DevOps, Machine Learning & Data Analysis"
        description="Comprehensive programming courses with personalized 1-on-1 mentorship. Learn Python, React, DevOps, Machine Learning, and Data Analysis from an experienced software engineer. Flexible scheduling worldwide."
        keywords="python course, react development course, devops training, machine learning course, data analysis course, programming classes, coding bootcamp, software development training, online programming courses"
        canonical="/courses"
        ogImage="/og-courses.jpg"
        structuredData={[
          ...courses.map(course => courseStructuredData(course)),
          faqStructuredData(coursesFAQ)
        ]}
      />
      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-700 to-cyan-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-6">
              All Courses
            </h1>
            <p className="text-xl md:text-2xl text-cyan-100 max-w-3xl mx-auto">
              Choose from our comprehensive range of programming and technology courses. 
              Each course is designed to take you from beginner to professional level.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="All">All Categories</option>
                {courseCategories.map(category => (
                  <option key={category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 dark:text-gray-400">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="name">Name</option>
                <option value="price">Price</option>
                <option value="duration">Duration</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-10">Course Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courseCategories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedCategory(category.name)}
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AcademicCapIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {category.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white">
              {selectedCategory === 'All' ? 'All Courses' : `${selectedCategory} Courses`}
            </h2>
            <span className="text-gray-600 dark:text-gray-400">
              {sortedCourses.length} course{sortedCourses.length !== 1 ? 's' : ''} found
            </span>
          </div>

          {sortedCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedCourses.map((course, index) => (
                <CourseCard key={course.id} course={course} index={index} />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <MagnifyingGlassIcon className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No courses found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search criteria or browse all categories.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Course Comparison */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 dark:text-white mb-4">
              Course Comparison
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Compare our courses to find the perfect fit for your learning goals.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Course</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Duration</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Level</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course, index) => (
                  <motion.tr
                    key={course.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{course.icon}</span>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {course.title}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {course.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {course.duration}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <AcademicCapIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {course.level}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {course.price}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
